import ReconnectingWebSocket from 'reconnecting-websocket';

// Client for the cf-backend (Cloudflare Worker + Durable Object) that drops the
// address-bound midnight claim token over a WebSocket, and serves the countdown
// time over HTTP. Replaces the token-drop + getTime parts of the old Node backend.

const HOST = 'isitalgorandsbirthday.com';

/** WebSocket base for cf-backend, env-switched the same way the old api.js did. */
export function dropWsBase() {
  if (import.meta.env.VITE_DROP_WS_URL) {
    return import.meta.env.VITE_DROP_WS_URL.replace(/\/+$/, '');
  }
  const { hostname } = window.location;
  if (hostname.endsWith(HOST)) {
    // production cf-backend (override with VITE_DROP_WS_URL once deployed)
    return `wss://drop.${hostname}`;
  }
  return `ws://${hostname}:8787`;
}

/** HTTP base for cf-backend (e.g. the /time endpoint), derived from the WS base. */
export function dropHttpBase() {
  return dropWsBase().replace(/^ws/, 'http');
}

/** GET cf-backend /time -> { time, bdayStart, bdayEnd } (seconds). */
export async function fetchServerTime() {
  const res = await fetch(`${dropHttpBase()}/time`);
  if (!res.ok) throw new Error(`time fetch failed: ${res.status}`);
  return res.json();
}

/**
 * GET cf-backend /nft/2026/tweet -> the URL the 2026 share-tweet quotes.
 * Returns '' when the worker has no URL configured (so the caller can omit the
 * quote line) or when the request fails.
 */
export async function fetchDaddyTweet() {
  try {
    const res = await fetch(`${dropHttpBase()}/nft/2026/tweet`);
    if (!res.ok) return '';
    const data = await res.json();
    return typeof data.daddyTweet === 'string' ? data.daddyTweet : '';
  } catch {
    return '';
  }
}

class DropClient {
  rws = null;
  address = null;
  turnstileToken = null;
  verified = false;
  discountToken = null;
  // Set by the TurnstileGate component: () => request a fresh widget token.
  onNeedToken = null;
  // Set by the buy page: (status) => 'pending' | 'verified' | 'failed'.
  onStatus = null;
  // Set by the app: (token) => called once when a brand-new drop token arrives,
  // so the UI can show a "token received" notification.
  onToken = null;
  // Latches true after the first token notification so we never show it twice,
  // even if the backend drops multiple (or repeated) tokens this session.
  tokenNotified = false;

  /** Wire the discount-token store the drop should write into. */
  init(discountTokenStore) {
    this.discountToken = discountTokenStore;
  }

  /** Track the connected wallet address; registration is gated on Turnstile. */
  setAddress(address) {
    const next = address || null;
    if (next === this.address) return;
    this.address = next;
    this.verified = false;
    if (!this.address) return; // keep any existing socket; nothing to register
    if (!this.rws) this.open();
    else this.tryRegister();
  }

  /** Feed a Turnstile token from the widget; triggers registration when ready. */
  setTurnstileToken(token) {
    this.turnstileToken = token || null;
    this.tryRegister();
  }

  /**
   * Register the address once we have an open socket + a Turnstile token. The
   * server verifies the token (siteverify) before the address becomes eligible
   * for the drop. If a token is missing, ask the widget for a fresh one.
   */
  tryRegister() {
    if (!this.rws || this.rws.readyState !== 1) return; // not open yet
    if (!this.address || this.verified) return;
    if (!this.turnstileToken) {
      if (this.onNeedToken) this.onNeedToken();
      return;
    }
    if (this.onStatus) this.onStatus('pending');
    this.send({ cmd: 'register', address: this.address, token: this.turnstileToken });
  }

  open() {
    // URL provider is re-read on every (re)connect, so it always uses the latest address.
    const urlProvider = () =>
      `${dropWsBase()}/ws${this.address ? `?address=${encodeURIComponent(this.address)}` : ''}`;
    this.rws = new ReconnectingWebSocket(urlProvider);
    this.rws.addEventListener('open', () => {
      // A reconnect is a brand-new (unverified) socket: re-register.
      this.verified = false;
      this.tryRegister();
    });
    this.rws.addEventListener('close', () => {
      this.verified = false;
      // Tokens are single-use/short-lived: never resend one across a reconnect.
      // Drop it so the next 'open' asks the widget for a fresh token instead.
      this.turnstileToken = null;
    });
    this.rws.addEventListener('message', this.onMessage);
    this.rws.addEventListener('error', (e) => console.log('dropClient error', e));
  }

  send(obj) {
    try {
      this.rws?.send(JSON.stringify(obj));
    } catch (e) {
      console.log('dropClient send error', e);
    }
  }

  onMessage = (msg) => {
    try {
      const data = JSON.parse(msg.data);
      switch (data.cmd) {
        case 'set-discount-token':
          if (data.token && this.discountToken && this.discountToken.token !== data.token) {
            // A token already present (e.g. persisted across a reload) means this
            // is a re-drop, not a first receipt — stay silent, like the old backend.
            const hadToken = !!this.discountToken.token;
            this.discountToken.setToken(data.token);
            // Notify exactly once per session, no matter how many tokens arrive.
            if (!hadToken && !this.tokenNotified) {
              this.tokenNotified = true;
              if (this.onToken) this.onToken(data.token);
            }
          }
          break;
        case 'registered':
          this.verified = !!data.verified;
          this.turnstileToken = null; // single-use; consumed by the server
          if (this.onStatus) this.onStatus(this.verified ? 'verified' : 'failed');
          break;
        case 'error':
          if (data.code === 'TURNSTILE') {
            // token invalid/expired — drop it and ask the widget for a new one.
            this.turnstileToken = null;
            if (this.onStatus) this.onStatus('failed');
            if (this.onNeedToken) this.onNeedToken();
          }
          break;
        default:
          break; // 'connected' / 'pong' are informational
      }
    } catch (e) {
      console.log('dropClient parse error', e);
    }
  };
}

const dropClient = new DropClient();
export default dropClient;
